import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email });
            setSent(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="forgot-password-page">
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back-login">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to login
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-bold mt-4">Forgot your password?</h1>
            <p className="text-muted-foreground mt-2">
                Enter the email you used to sign up. We'll send you a link to reset your password.
            </p>

            {sent ? (
                <div className="mt-8 rounded-2xl border border-primary/20 bg-primary/5 p-6 text-sm" data-testid="forgot-sent-confirmation">
                    <Mail className="w-6 h-6 text-primary" />
                    <div className="font-display text-lg font-semibold mt-3">Check your inbox</div>
                    <p className="text-muted-foreground mt-2">
                        If an account exists for <strong>{email}</strong>, we've just sent a reset link. It's valid for 60 minutes. Don't forget to check spam.
                    </p>
                </div>
            ) : (
                <form onSubmit={submit} className="mt-8 bg-white border border-border/60 rounded-2xl p-6 space-y-4">
                    <div>
                        <Label htmlFor="femail">Email</Label>
                        <Input id="femail" type="email" required data-testid="forgot-email-input"
                            className="mt-1 rounded-xl h-11" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <Button type="submit" disabled={loading || !email} data-testid="forgot-submit-btn"
                        className="w-full rounded-full h-11 bg-primary hover:bg-primary/90">
                        {loading ? "Sending…" : "Send reset link"}
                    </Button>
                </form>
            )}
        </div>
    );
}
